import { Store } from '@tanstack/store';

import { mergeYearOptions } from '../lib/year-options';

/**
 * The year every screen reads.
 *
 * There used to be a year dropdown per screen — analytics kept it in the URL,
 * dashboard widgets each carried their own in widget config, the owner portal
 * and the competitor-set page held local state. Four sources meant four answers
 * to the same question. This is the single one; the header owns the control.
 */

const STORAGE_KEY = 'selectedYear';

/**
 * A sanity guard, not the list of years on offer. Which years are worth showing
 * depends on the organisation's data (see mergeYearOptions), and that arrives
 * over the network — so a stored or deep-linked year must not be rejected just
 * because the data has not loaded yet.
 */
export function isSelectableYear(year: number): boolean {
  const currentYear = new Date().getFullYear();
  return Number.isInteger(year) && year >= 2000 && year <= currentYear + 10;
}

/**
 * The stored year, or the current year. Storage can be empty, hold junk from an
 * older build, or throw outright (private windows, blocked site data) — none of
 * which should leave someone staring at a screen with no data.
 */
export function readStoredYear(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = Number(raw);
    if (raw !== null && isSelectableYear(parsed)) return parsed;
  } catch {
    // ignore unavailable storage
  }
  return new Date().getFullYear();
}

interface YearStoreState {
  year: number;
}

export const yearStore = new Store<YearStoreState>({ year: readStoredYear() });

export function getYear(): number {
  return yearStore.state.year;
}

/** Ignores an unselectable year rather than propagating it to every screen. */
export { mergeYearOptions };

export function setYear(year: number): void {
  if (!isSelectableYear(year)) return;

  yearStore.setState((prev) => ({ ...prev, year }));
  try {
    localStorage.setItem(STORAGE_KEY, String(year));
  } catch {
    // ignore unavailable storage — the year still applies for this session
  }
}
